import { useGroupClassRegistration } from "@/hooks/useGroupClassRegistration";
import { Button, FAIconName } from "@/ui/core";
import type { ButtonSize, ButtonVariant } from "@/ui/core/buttons/Button";
import { shouldHideRegistrationStatus } from "@/utils/groupClass.util";

interface RegisterButtonProps extends BaseViewProps {
  forWhom?: string;
  groupClassExternalId: string;
  isAppUserSupport?: boolean;
  isButtonDisabled?: boolean;
  isFullWidth?: boolean;
  isLoading?: boolean;
  isOnPressDisabled?: boolean;
  isRegistered?: boolean;
  isSensitive?: boolean;
  onRegistrationChange?: ({
    isRegistered,
    wasRegistered,
  }: {
    isRegistered: boolean;
    wasRegistered: boolean;
  }) => void;
  patientExternalId: string;
  size?: ButtonSize;
}

const getSensitiveDetails = ({
  forWhom,
  isAppUserSupport,
  isButtonDisabled,
  isRegistered,
  isSensitive,
}: Pick<
  RegisterButtonProps,
  | "forWhom"
  | "isAppUserSupport"
  | "isButtonDisabled"
  | "isRegistered"
  | "isSensitive"
>): {
  isButtonDisabled?: boolean;
  leftIcon: FAIconName;
  title: string;
  variant: ButtonVariant;
} => {
  const unregisteredText = `Register ${forWhom?.trim()}`.trim();

  // if the parent Group Class object is sensitive AND the current user is a support
  // we should not display whether the patient is registered, to protect their privacy
  // instead we should always show the button status as unregistered
  const shouldShowAsUnregistered = shouldHideRegistrationStatus({
    isAppUserSupport,
    isSensitive,
  });
  const shouldBeDisabled = isButtonDisabled || shouldShowAsUnregistered;
  const shouldShowRegistered = isRegistered && !shouldShowAsUnregistered;

  const [title, leftIcon, variant]: [string, FAIconName, ButtonVariant] =
    shouldShowRegistered
      ? ["Registered", FAIconName.Check, "secondary"]
      : [unregisteredText, FAIconName.UserPlus, "primary"];

  return {
    isButtonDisabled: shouldBeDisabled,
    leftIcon,
    title,
    variant,
  };
};

const RegisterButton = ({
  accessibilityLabel,
  forWhom = "for group",
  groupClassExternalId,
  isAppUserSupport,
  isButtonDisabled: isButtonDisabledInitially,
  isFullWidth,
  isLoading: isButtonLoading,
  isOnPressDisabled,
  isRegistered,
  isSensitive,
  onRegistrationChange,
  patientExternalId,
  size = "normal",
  testID,
}: RegisterButtonProps) => {
  const {
    isLoading: isRegistrationMutationLoading,
    registerForGroupClass,
    unregisterForGroupClass,
  } = useGroupClassRegistration({
    groupClassExternalId,
    patientExternalId,
  });
  const isLoading = isButtonLoading || isRegistrationMutationLoading;
  const onPress = () => {
    if (isOnPressDisabled) return;
    if (isRegistered) {
      unregisterForGroupClass(onRegistrationChange);
    } else {
      registerForGroupClass(onRegistrationChange);
    }
  };

  const { isButtonDisabled, leftIcon, title, variant } = getSensitiveDetails({
    forWhom,
    isAppUserSupport,
    isButtonDisabled: isButtonDisabledInitially,
    isRegistered,
    isSensitive,
  });

  return (
    <Button
      accessibilityLabel={accessibilityLabel}
      isActive={isRegistered}
      isDisabled={isButtonDisabled}
      isFullWidth={isFullWidth}
      isLoading={isLoading}
      leftIcon={leftIcon}
      onPress={onPress}
      size={size}
      testID={testID}
      title={title}
      variant={variant}
    />
  );
};

export type { RegisterButtonProps };
export default RegisterButton;
